import { computeEmi } from './EmiCalculator';

/**
 * Build a year-by-year schedule of interest vs principal from a fixed EMI loan.
 * Each year sums the 12 monthly interest / principal components.
 */
export function buildEmiYearlySplit(loanAmount, annualInterestRatePct, tenureYears) {
  const P = Number(loanAmount);
  const years = Math.round(Number(tenureYears));
  const annualRate = Number(annualInterestRatePct);

  if (!(P > 0) || !(years > 0) || !Number.isFinite(annualRate)) {
    return {
      monthlyEmi: 0,
      annualEmi: 0,
      totalInterest: 0,
      totalPrincipal: 0,
      totalPaid: 0,
      yearRows: [],
    };
  }

  const n = years * 12;
  const monthlyEmi = computeEmi(P, annualRate, years);
  const r = annualRate > 0 ? annualRate / 12 / 100 : 0;

  let balance = P;
  const yearRows = [];
  let totalInterest = 0;
  let totalPrincipal = 0;

  for (let year = 1; year <= years; year += 1) {
    let interestYear = 0;
    let principalYear = 0;

    for (let m = 0; m < 12; m += 1) {
      const monthIndex = (year - 1) * 12 + m;
      if (monthIndex >= n || balance <= 0.5) break;

      const interestPart = r > 0 ? balance * r : 0;
      let principalPart = monthlyEmi - interestPart;

      if (principalPart < 0) principalPart = 0;
      if (principalPart > balance) principalPart = balance;

      // Final payment: clear residual principal if EMI overshoots slightly.
      if (monthIndex === n - 1 && balance - principalPart > 0 && balance - principalPart < 1) {
        principalPart = balance;
      }

      interestYear += interestPart;
      principalYear += principalPart;
      balance = Math.max(0, balance - principalPart);
    }

    const annualTotal = interestYear + principalYear;
    yearRows.push({
      year,
      interest: interestYear,
      principal: principalYear,
      annualTotal,
      interestSharePct: annualTotal > 0 ? (interestYear / annualTotal) * 100 : 0,
      principalSharePct: annualTotal > 0 ? (principalYear / annualTotal) * 100 : 0,
      closingBalance: balance,
    });

    totalInterest += interestYear;
    totalPrincipal += principalYear;
  }

  return {
    monthlyEmi,
    annualEmi: monthlyEmi * 12,
    totalInterest,
    totalPrincipal,
    totalPaid: totalInterest + totalPrincipal,
    yearRows,
  };
}
